import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Clock, Calendar, Users, ArrowRight } from 'lucide-react';

const USBenchTest = () => {
  const benchTestTasks = [
    "Crown preparation procedures",
    "Class II amalgam restoration",
    "Class III composite restoration",
    "Endodontic access cavity",
    "Wax carving and anatomy",
    "Occlusion and articulation"
  ];

  const trainingFeatures = [
    "Intensive hands-on practice",
    "Simulation-based learning",
    "Time management training",
    "Quality assessment criteria",
    "Mock bench test examinations",
    "Technical skill refinement",
    "Materials and equipment provided",
    "Expert faculty guidance"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              US Bench Test Course
            </h1>
            <p className="text-lg md:text-xl text-gray-100 mb-8">
              Focused preparation for US dental licensing bench test examination with emphasis on technical precision and time management
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" variant="secondary" className="bg-white text-[#1e3a8a] hover:bg-gray-100">
                <Link to="/contact">Enroll Now</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Link to="/courses">View All Courses</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Course Details */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6">
                <Clock className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">3-4 Weeks</div>
                <div className="text-sm text-gray-600">Duration</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Calendar className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">Mon-Fri</div>
                <div className="text-sm text-gray-600">10 AM - 7 PM</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Calendar className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">Saturday</div>
                <div className="text-sm text-gray-600">10 AM - 2 PM</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Users className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">Full-time</div>
                <div className="text-sm text-gray-600">Intensive Training</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About US Bench Test */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                US Dental Licensing Bench Test
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                The US dental licensing bench test is a critical component of obtaining dental licensure in the United States. This examination assesses your technical skills, manual dexterity, and understanding of dental procedures through hands-on tasks.
              </p>
              <p className="text-gray-600 mb-6">
                Our specialized training program focuses on developing the precision, speed, and consistency required to excel in the bench test examination. We provide intensive practice with authentic materials and equipment.
              </p>
              <Button asChild className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90">
                <Link to="/contact">Start Your Preparation <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="bg-[#1e3a8a]/5 rounded-2xl p-8 border-2 border-[#1e3a8a]/20">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Bench Test Tasks</h3>
              <div className="space-y-3">
                {benchTestTasks.map((task, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{task}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Training Approach */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Training Approach
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Systematic preparation focused on technical excellence and exam readiness
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trainingFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <CheckCircle className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <p className="text-gray-700 font-medium">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why ODA for US Bench Test Preparation
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border-2 border-gray-100">
              <div className="text-4xl font-bold text-[#1e3a8a] mb-4">01</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Technical Precision
              </h3>
              <p className="text-gray-600">
                Focus on developing the exact technical skills and precision required for US bench test success
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border-2 border-gray-100">
              <div className="text-4xl font-bold text-[#1e3a8a] mb-4">02</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Time Management
              </h3>
              <p className="text-gray-600">
                Intensive training in completing procedures within strict time constraints while maintaining quality
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border-2 border-gray-100">
              <div className="text-4xl font-bold text-[#1e3a8a] mb-4">03</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Quality Assessment
              </h3>
              <p className="text-gray-600">
                Understanding evaluation criteria and consistent achievement of required quality standards
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Training Coverage */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-6">Crown Preparation</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Full coverage crown preparation techniques</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Margin placement and finishing</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Retention and resistance form</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Axial wall reduction and taper</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Occlusal clearance verification</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-6">Restorative Procedures</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Class II amalgam with proper contours</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Class III composite with esthetics</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Cavity preparation design</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Matrix placement and adaptation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Finishing and polishing techniques</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Success Strategy */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#1e3a8a] text-white rounded-2xl p-8 md:p-12">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Your Path to Success
              </h2>
              <p className="text-lg text-gray-200 mb-8">
                Our proven methodology combines intensive practice, expert feedback, and strategic preparation to ensure you're fully ready for the US bench test examination.
              </p>
              <div className="grid md:grid-cols-3 gap-6 text-left">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <div className="text-3xl font-bold mb-2">Week 1-2</div>
                  <p className="text-sm text-gray-200">Foundation building and technique development</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <div className="text-3xl font-bold mb-2">Week 3</div>
                  <p className="text-sm text-gray-200">Speed and precision refinement</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <div className="text-3xl font-bold mb-2">Week 4</div>
                  <p className="text-sm text-gray-200">Mock exams and final preparation</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Begin Your US Bench Test Preparation
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Take the first step toward US dental licensure with expert training
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90">
              <Link to="/contact">Enroll Now <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white">
              <Link to="/courses">View All Courses</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default USBenchTest;
